import { t } from '@lingui/core/macro'
import { useLingui } from '@lingui/react'
import { Button } from 'designSystem/components/Button'
import { RevisionCompareView } from 'features/knowledgeDocumentViewer/components/revisionCompare/RevisionCompareView'
import {
  diffTouchesMember,
  filterDetailsToMembers,
} from 'features/knowledgeDocumentViewer/components/revisionCompare/revisionDiffModel'
import type { CanonicalRevisionModel } from 'features/knowledgeDocumentViewer/components/revisionCompare/revisionMemberCanonical'
import { useKnowledgeDocumentDataContext } from 'features/knowledgeDocumentViewer/contexts/KnowledgeDocumentDataContext'
import { useRevisionDiffDetailsForDocument } from 'features/knowledgeDocumentViewer/hooks/useRevisionDiffDetails'
import { GitCompare } from 'lucide-react'
import { useMemo, useState } from 'react'

type RevisionCompareLauncherProps = {
  bqDocumentId: string
  pageNum: number
  canonicalRevisionModel: CanonicalRevisionModel
}

export function RevisionCompareLauncher({
  bqDocumentId,
  pageNum,
  canonicalRevisionModel,
}: RevisionCompareLauncherProps) {
  useLingui()
  const { siteID } = useKnowledgeDocumentDataContext()
  const { canonicalEndpoint } = canonicalRevisionModel
  const { details } = useRevisionDiffDetailsForDocument(
    canonicalRevisionModel.familyDocumentIds,
    siteID
  )
  const [isCompareOpen, setIsCompareOpen] = useState(false)

  const familyDetails = useMemo(
    () =>
      filterDetailsToMembers(
        details,
        canonicalRevisionModel.familyMembers,
        canonicalEndpoint
      ),
    [details, canonicalRevisionModel.familyMembers, canonicalEndpoint]
  )

  const pageDetails = familyDetails.filter((detail) =>
    diffTouchesMember(detail.data, bqDocumentId, pageNum, canonicalEndpoint)
  )

  if (pageDetails.length === 0) return null

  return (
    <>
      <Button
        type='button'
        variant='inputRaised'
        size='lg'
        aria-label={t`What changed`}
        onClick={() => setIsCompareOpen(true)}>
        <GitCompare size={18} strokeWidth={1.5} />
        {t`What changed`}
      </Button>
      <RevisionCompareView
        open={isCompareOpen}
        onOpenChange={setIsCompareOpen}
        details={familyDetails}
        canonicalRevisionModel={canonicalRevisionModel}
        currentBqDocumentId={bqDocumentId}
        currentPageNum={pageNum}
      />
    </>
  )
}
